import { useEffect, useMemo, useRef, useState } from 'react'

type TabLike = { value: string; label: string; className?: string }

const DEFAULT_TAB_WIDTH = 120

/**
 * Hook to handle tab overflow in a responsive way.
 * Calculates which tabs should be visible vs hidden in an overflow popover.
 */
export function useTabsOverflow({
  tabs,
  activeValue,
}: {
  tabs: TabLike[]
  activeValue: string
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const measureRef = useRef<Map<string, number>>(new Map())
  const [tabWidths, setTabWidths] = useState<Record<string, number>>({})
  const [moreBtnWidth, setMoreBtnWidth] = useState(64)
  const [containerWidth, setContainerWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth * 0.65 : 0
  )

  const registerWidth = (value: string, el: HTMLElement | null) => {
    if (!el) return
    const w = Math.ceil(el.getBoundingClientRect().width)
    if (measureRef.current.get(value) !== w) {
      measureRef.current.set(value, w)
      setTabWidths(Object.fromEntries(measureRef.current))
    }
  }

  const { visible, overflowed } = useMemo(() => {
    let used = 40 // Initial padding
    const visible: string[] = []

    for (let i = 0; i < tabs.length; i++) {
      const t = tabs[i]
      const w = tabWidths[t.value] ?? DEFAULT_TAB_WIDTH
      const gap = visible.length === 0 ? 0 : 8
      const remaining = tabs.length - (i + 1)
      const willNeedMore = remaining > 0
      const guard = willNeedMore ? moreBtnWidth : 0
      const nextUsed = used + gap + w

      if (nextUsed + guard <= containerWidth) {
        visible.push(t.value)
        used = nextUsed
      } else {
        break
      }
    }

    // Ensure active tab is always visible
    if (!visible.includes(activeValue)) {
      const activeTabWidth = tabWidths[activeValue] ?? DEFAULT_TAB_WIDTH
      const visibleTabWidths = visible.map(
        (v) => tabWidths[v] ?? DEFAULT_TAB_WIDTH
      )

      let usedSpace = 40
      for (let i = 0; i < visible.length - 1; i++) {
        usedSpace += (i === 0 ? 0 : 8) + visibleTabWidths[i]
      }

      while (
        visible.length > 0 &&
        usedSpace +
          (visible.length > 1 ? 8 : 0) +
          activeTabWidth +
          moreBtnWidth >
          containerWidth
      ) {
        usedSpace -=
          (visible.length > 1 ? 8 : 0) + visibleTabWidths[visible.length - 1]
        visible.pop()
        visibleTabWidths.pop()
      }

      if (visible.length > 0) {
        visible[visible.length - 1] = activeValue
      } else {
        visible.push(activeValue)
      }
    }

    const overflowed = tabs
      .filter((t) => !visible.includes(t.value))
      .map((t) => t.value)
    return { visible, overflowed }
  }, [tabs, activeValue, tabWidths, containerWidth, moreBtnWidth])

  const setMoreRef = (el: HTMLElement | null) => {
    if (!el) return
    const w = Math.ceil(el.getBoundingClientRect().width)
    if (w && w !== moreBtnWidth) setMoreBtnWidth(w)
  }

  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(window.innerWidth * 0.65)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return { containerRef, registerWidth, setMoreRef, visible, overflowed }
}
